import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { Layout, message } from 'antd';
import { PlayerProvider } from '@context/PlayerContext';
import MediaViewerHeader from '@components/MediaViewerHeader';
import { useDispatch } from 'react-redux';
import {
  createNewComment,
  removeComment,
  editComment,
  setCommentLike,
  setAssetVariant,
  setAsset,
  removeCommentAttachment,
  setCommentResolve
} from '@redux/actions/mediaViewerAction';
import {
  addNotificationSubscription,
  addReviewVisit,
  createCommentAssetReview,
  createReplyCommentAssetReview,
  deleteCommentAssetReview,
  getCommentAssetReview,
  getPotentialMentionsAssetReview,
  setCommentLikeAssetReview,
  setCommentResolveAssetReview,
  updateCommentAssetReview
} from '@api/AssetReview';
import {
  ReplyAssetReviewCommentDto,
  CreateAssetReviewCommentDto
} from '@api/Api';
import { useAssetReview } from '@components/AssetReviewProvider';
import { useTypedSelector } from '@hooks';
import { nonNull } from '@helpers/non-null';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import { useAuth } from '@hooks/useAuth';
import { apiClient } from '@api/client';
import { useCommentsAssetReviewQuery } from '@hooks/queries/assetReview';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useAssetByReviewQuery } from '@hooks/queries/assets';
import { useLayoutEffectWithPrev } from '@hooks/useEffectWithPrev';
import { showNewAssetVersionToast } from '@helpers/showNewAssetVersionToast';

const { Header, Content } = Layout;

function ExternalReviewerMediaLayout() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const { content, reviewId, password, updateExternalContent, isPreview } =
    useAssetReview();
  const assetIdParam = searchParams.get('assetId');
  const versionIdParam = searchParams.get('versionId');
  const assetQuery = useAssetByReviewQuery(
    { assetId: assetIdParam ?? '', reviewId: reviewId ?? '', password },
    {
      enabled: !!assetIdParam,
      placeholderData: content?.assets.find((x) => x.id === assetIdParam)
    }
  );
  const asset = assetQuery.data ?? null;
  const assetId = asset?.id;
  const prevVersionId = useRef(selectedVersion?.id || null);

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useCommentsAssetReviewQuery(
      {
        assetVersionId: selectedVersion?.id || '',
        assetReviewId: reviewId || '',
        assetReviewPassword: password
      },
      {
        keepPreviousData: selectedVersion?.id === prevVersionId.current,
        enabled: !!selectedVersion?.id && !!reviewId
      }
    );

  if (selectedVersion?.id !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = selectedVersion?.id || null;
  }

  useEffect(() => {
    if (isPreview) return;
    if (user)
      addNotificationSubscription({
        reviewId: nonNull(reviewId),
        password
      });
    addReviewVisit({
      reviewId: nonNull(reviewId),
      password
    });
  }, [user, reviewId, password, isPreview]);

  const outletContext = useMemo<MediaViewerOutletContextState>(() => {
    const params = {
      assetReviewId: nonNull(reviewId),
      assetReviewPassword: password
    };
    return {
      addNewComment: async (data, parentId) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await createReplyCommentAssetReview({
              ...(data as ReplyAssetReviewCommentDto),
              ...params
            });
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await createCommentAssetReview({
              ...(data as CreateAssetReviewCommentDto),
              ...params
            });
            newComment = comment;
          }
          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id) => {
        try {
          await removeComment(id);
          await deleteCommentAssetReview({ id, ...params });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data) => {
        try {
          const {
            data: { comment }
          } = await updateCommentAssetReview({
            ...data,
            ...params
          });
          await editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data, user) => {
        try {
          await setCommentLike(data, user);
          await setCommentLikeAssetReview({
            ...data,
            ...params
          });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentResolve: async (data, user) => {
        await setCommentResolve(data, user);
        await setCommentResolveAssetReview({
          ...data,
          ...params
        });
      },
      getPotentialMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } = await getPotentialMentionsAssetReview({
          assetVersionId,
          searchQuery,
          ...params
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } =
          await apiClient.assetReview.assetReviewCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery,
              ...params
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsList || []
    };
  }, [reviewId, password, isLoadingCommentsList, commentsList]);

  useLayoutEffect(() => {
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_STATE
    });
  }, [assetIdParam, dispatch]);

  useLayoutEffect(() => {
    if (!selectedVersion?.id) return;
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_VERSION_STATE
    });
  }, [dispatch, versionIdParam]);

  useLayoutEffectWithPrev(
    ([prevAsset, prevVersionId]) => {
      dispatch(setAsset(asset));
      const version =
        asset?.asset?.versions.find((v) => v.id === versionIdParam) ??
        asset?.asset?.versions[0];
      dispatch(setAssetVariant(version?.id ?? null));

      let newVersionAdded = false;
      let newVersionAddedByMe = false;
      if (prevAsset?.id === asset?.id) {
        const optimisticUpdate =
          !!(prevAsset as any)?.versionsOptimisticUpdate ||
          !!(asset as any)?.versionsOptimisticUpdate;
        const prevVersionIds = (prevAsset?.asset?.versions ?? []).map(
          (v) => v.id
        );
        const newVersionIds = (asset?.asset?.versions ?? []).map((v) => v.id);
        newVersionAdded =
          !optimisticUpdate &&
          newVersionIds[0] !== prevVersionId &&
          newVersionIds.some((id) => !prevVersionIds.includes(id));
        if (newVersionAdded) {
          const newVersion = asset?.asset?.versions.find(
            (v) => !prevVersionIds.includes(v.id)
          );
          newVersionAddedByMe = !!newVersion?.user?.me;
        }
      }

      if (asset && version?.id !== versionIdParam) {
        const version = asset?.asset?.versions[0];
        setSearchParams(
          (prev) => {
            prev.set('versionId', version?.id ?? '');
            return prev;
          },
          { replace: true }
        );
      } else if (newVersionAdded) {
        const goToLatestVersion = () =>
          setSearchParams((prev) => {
            prev.delete('versionId');
            return prev;
          });
        if (newVersionAddedByMe) goToLatestVersion();
        else showNewAssetVersionToast(nonNull(assetId), goToLatestVersion);
      }
    },
    [asset, versionIdParam, dispatch]
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== asset?.id) return false;
    if (msg.payload.assetVersionId !== selectedVersion?.id) return false;
    if (msg.causedByMe) return;
    const {
      data: { comment }
    } = await getCommentAssetReview({
      id: msg.payload.commentId,
      assetReviewId: nonNull(reviewId),
      assetReviewPassword: password
    });
    await createNewComment(comment, selectedVersion?.id || '');
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== asset?.id) return false;
    if (msg.payload.assetVersionId !== selectedVersion?.id) return false;
    if (msg.causedByMe) return;

    await removeComment(msg.payload.commentId);
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== asset?.id) return;
      const {
        data: { comment }
      } = await getCommentAssetReview({
        id: lastMessage.payload.target.commentId,
        assetReviewId: nonNull(reviewId),
        assetReviewPassword: password
      });
      await editComment(comment);
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== asset?.id) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== asset?.id) return;
    if (msg.causedByMe) return;
    await removeCommentAttachment({
      commentId: msg.payload.target.commentId,
      attachmentId: msg.payload.attachmentId
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];

      const {
        data: { comment }
      } = await getCommentAssetReview({
        id: lastMessage.payload.commentId,
        assetReviewId: nonNull(reviewId),
        assetReviewPassword: password
      });
      await editComment(comment);
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== asset?.id) return false;
      if (msg.payload.assetVersionId !== selectedVersion?.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    () => assetQuery.refetch(),
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  return (
    <Layout
      style={{ background: '#1A1C1E', color: '#fff', minHeight: '100vh' }}
    >
      <Layout
        className="site-layout external-reviewer-media"
        style={{ background: '#1A1C1E' }}
      >
        <Header
          className="site-layout-background b-background-black"
          style={{
            padding: 0,
            width: '100%'
          }}
        >
          <MediaViewerHeader view="external-share-asset" />
        </Header>
        <Content style={{ marginTop: 70 }}>
          <PlayerProvider>
            <Outlet context={outletContext} />
          </PlayerProvider>
        </Content>
      </Layout>
    </Layout>
  );
}

export default ExternalReviewerMediaLayout;
